/**
 * The one place that decides what a route's ``<head>`` says.
 *
 * The site publishes its metadata twice. ``scripts/generate-static-meta.mjs``
 * bakes 251 documents for crawlers — ``SEO.md`` §3.1 records that the served
 * ``<body>`` is empty, so those files *are* the page as far as a search engine
 * is concerned — and ``src/components/LanguageRouter`` writes the same facts
 * into ``document.head`` for the visitor who arrives afterwards. Before this
 * module the two computed the title, the description, the Open Graph block and
 * the six ``hreflang`` alternates from separate code, and nothing bound them:
 * metadata maintained apart from the page drifts apart within one release
 * cycle. That is SEO backlog item ``B5``; this module is its fix.
 *
 * **The seam is a pathname, not a ``(lang, routePath)`` pair**, and that is the
 * one choice here worth explaining. A locale-plus-route pair looks like the
 * tidier interface, but the runtime does not have one — it has
 * ``location.pathname``, and for a path whose first segment is not a locale
 * (``/insights``) stripping the locale yields an *empty* route. The canonical
 * would then move from ``https://shelpuk.com/insights`` to
 * ``https://shelpuk.com/en``: a different page. Taking the pathname keeps
 * ``toCanonicalUrl(location.pathname)`` verbatim on both sides — the generator
 * composes the pathname it already holds, which round-trips exactly — and the
 * hazard is removed rather than tested for.
 *
 * **What it imports rather than owns.** :func:`toCanonicalUrl` and
 * ``SITE_ORIGIN`` come from :mod:`src/lib/urlNormalise` (``B3``/``E3.2``, which
 * owns the URL *form*), image sizes from :mod:`src/lib/ogImageDimensions`
 * (``E2.4``), image paths from :mod:`src/data/ogImages`, and the locale tables
 * from :mod:`src/lib/seoMetadata`. Re-implementing any of them would re-open
 * the backlog item that consolidated it.
 *
 * **No ``@/`` import, here or anywhere in this module's graph.**
 * ``generate-static-meta.mjs`` loads this through ``importTsModule``, whose
 * transpiler rewrites *relative* specifiers only — and does not *report* the
 * ones it cannot rewrite. An aliased import would survive to ``import()`` and
 * fail at module load, breaking ``npm run build`` while Vite and Vitest stayed
 * green. ``src/lib/headMeta.test.ts`` asserts the absence.
 *
 * ``.system_design/SYSTEM_DESIGN.md`` §13c carries the decision and the
 * alternatives rejected; ``SEO.md`` ``I11`` is the invariant this makes
 * provable.
 */

import { getOgImageForRoute } from "../data/ogImages";
import { blogPosts, getLocalizedField } from "../data/blogPosts";
import { getLocalizedCaseStudyBySlug } from "../data/caseStudies";
import { STANDARD_OG_IMAGE_DIMENSIONS, type OgImageDimensions } from "./ogImageDimensions";
import {
  ORGANIZATION_NAME,
  ROOT_DOCUMENT_DESCRIPTION,
  ROOT_DOCUMENT_TITLE,
  SEO_LANGUAGES,
  TITLE_BRAND,
  getAlternateOgLocales,
  getDefaultOgImagePath,
  getOgLocale,
  normalizeSeoLanguage,
  type SeoLanguage,
} from "./seoMetadata";
import { SITE_ORIGIN, toCanonicalUrl } from "./urlNormalise";

/**
 * The Twitter card type every page declares.
 *
 * ``summary_large_image`` for the whole site: every page names a 1.91:1 Open
 * Graph image, which is the card this type renders.
 */
export const TWITTER_CARD_TYPE = "summary_large_image";

/** One ``<link rel="alternate">`` — a locale and the URL of its equivalent page. */
export interface HreflangAlternate {
  /** The ``hreflang`` value: an SEO language, or ``"x-default"``. */
  hreflang: string;
  /** The absolute, canonical URL of that locale's equivalent page. */
  href: string;
}

/** A pathname split into the locale it names and the route beneath it. */
export interface LocalePathParts {
  /** The locale, normalised — English for anything unrecognised. */
  lang: SeoLanguage;
  /** The route beneath the locale, with no leading or trailing slash. */
  routePath: string;
}

/**
 * The copy a route's title and description are drawn from.
 *
 * The shape of the ``meta`` key in ``src/i18n/locales/<lang>.json``. It is the
 * **only** injected input, because it is the only one the two emitters read
 * differently — the generator parses the file, the runtime asks i18next. Every
 * other fact this module needs it imports directly.
 */
export interface RouteCopy {
  /** The locale home page's title, used verbatim without the brand suffix. */
  homeTitle?: string;
  /** The description used for the home page and as the fallback everywhere. */
  homeDescription?: string;
  /** Per-route titles and descriptions, keyed by locale-stripped route path. */
  pages?: Record<string, { title?: string; description?: string } | undefined>;
}

/** Everything a ``<head>`` declares for one route in one locale. */
export interface HeadMeta {
  /** The locale, and the value of the document's ``lang`` attribute. */
  lang: SeoLanguage;
  /** The route beneath the locale, with no leading or trailing slash. */
  routePath: string;
  /** The document title, brand suffix included. */
  title: string;
  /** The meta description. */
  description: string;
  /** The canonical URL, and the value of ``og:url``. */
  canonicalUrl: string;
  /** The Open Graph image's public path, which is what keys its size. */
  ogImagePath: string;
  /** The same image as an absolute URL, for social crawlers. */
  ogImageUrl: string;
  /** The image's measured size, from the module that owns the measurements. */
  ogImageDimensions: OgImageDimensions;
  /** ``"article"`` for a resolved blog post, ``"website"`` otherwise. */
  ogType: "article" | "website";
  /** This page's Open Graph locale, e.g. ``de_DE``. */
  ogLocale: string;
  /** The other four locales, for ``og:locale:alternate``. */
  alternateOgLocales: string[];
  /** The five ``hreflang`` alternates plus ``x-default``. */
  alternates: HreflangAlternate[];
  /**
   * The organisation's full legal name, for ``og:site_name``.
   *
   * Deliberately **not** the title brand. CWS-128 shortened the ``<title>``
   * suffix to ``Shelpuk`` to stop boilerplate eating a search result's width
   * budget, and left this field as the legal name because it identifies the
   * publisher rather than competing for that budget.
   */
  siteName: string;
  /** The Twitter card type. */
  twitterCard: string;
}

/**
 * Split a pathname into the locale it names and the route beneath it.
 *
 * The first path segment is treated as the locale slot whatever it holds, and
 * anything unrecognised normalises to English. A path with **no** locale
 * prefix therefore loses its first segment — ``/insights`` yields
 * ``("en", "")`` — which is deliberate rather than an oversight: it is what
 * ``LanguageRouter`` has always done, and its first effect redirects such a
 * path to ``/en/insights`` before a visitor sees the result. It is also why
 * :func:`buildHeadMeta` takes a pathname; see this module's own docstring.
 *
 * Args:
 *     pathname: A URL path, in any form — leading, trailing and repeated
 *         separators are all tolerated.
 *
 * Returns:
 *     The locale and the route path beneath it.
 */
export function splitLocalePath(pathname: string): LocalePathParts {
  const segments = pathname.split("/").filter(Boolean);

  return {
    lang: normalizeSeoLanguage(segments[0]),
    routePath: segments.slice(1).join("/"),
  };
}

/**
 * Build the ``hreflang`` alternates for a route, in every supported locale.
 *
 * Each alternate is composed from the **locale-stripped route path**, so the
 * locale is prefixed rather than substituted. That is what makes the builder
 * prefix-safe: the previous runtime used ``path.replace("/" + lang, "/" + l)``,
 * and ``String.prototype.replace`` with a string argument replaces the first
 * occurrence *anywhere* — which mangled the slug of any path whose first
 * segment was not a locale (``SEO.md`` ``I5a``). There is no substitution here
 * to get wrong.
 *
 * ``x-default`` names the root document itself **only for the root
 * cluster** (``routePath === ""``). Deep pages keep ``/en/<route>``, which is
 * what ``SEO.md`` ``D3`` records and what the sitemap per-entry emission
 * agrees through. The single-line carve-out is the one place the root
 * cluster's fallback lives — the runtime emitter, the build-time generator,
 * and the sitemap all read from this function. CWS-237 changed the
 * **page** the root serves (the English homepage experience, with
 * auto-redirect on the client) but the cluster shape — bare origin as the
 * self-canonical ``x-default`` — is preserved.
 *
 * Args:
 *     routePath: The route beneath the locale, in any slash form.
 *
 * Returns:
 *     Five alternates in ``SEO_LANGUAGES`` order, then ``x-default``.
 */
export function buildHreflangAlternates(routePath: string): HreflangAlternate[] {
  // Through `toCanonicalUrl` rather than composed here: it normalises away the
  // empty segment a bare locale home leaves behind, so the builder is total
  // over every form its two callers hold the route path in.
  const localise = (lang: string) => toCanonicalUrl(`/${lang}/${routePath}`);
  const xDefaultHref = routePath === "" ? toCanonicalUrl("/") : localise("en");

  return [
    ...SEO_LANGUAGES.map((lang) => ({ hreflang: lang, href: localise(lang) })),
    { hreflang: "x-default", href: xDefaultHref },
  ];
}

/**
 * The metadata for a route that resolved none of its own.
 *
 * The title is the organisation's **legal name**, not the title brand. That is
 * a CWS-128 decision worth stating rather than inferring: the brand was 32
 * characters until that step shortened it to ``Shelpuk``, so it read as a title
 * on its own then and does not now. A page whose slug matched nothing should
 * still name its publisher.
 *
 * Args:
 *     homeDescription: The locale's home description, used as the fallback.
 *
 * Returns:
 *     The fallback title and description; never an article.
 */
function unresolved(homeDescription: string): {
  title: string;
  description: string;
  isArticle: boolean;
} {
  return { title: ORGANIZATION_NAME, description: homeDescription, isArticle: false };
}

/**
 * Resolve the title and description for a route from its locale copy.
 *
 * Args:
 *     routePath: The route beneath the locale.
 *     lang: The normalised locale.
 *     copy: The locale's ``meta`` copy.
 *
 * Returns:
 *     The title, the description, and whether the route resolved to a blog
 *     post — which is the only thing that makes a page an ``article``.
 */
function resolveCopy(
  routePath: string,
  lang: SeoLanguage,
  copy: RouteCopy,
): { title: string; description: string; isArticle: boolean } {
  const homeDescription = copy.homeDescription || "";

  // The locale home page. Its title stands alone — the locale copy writes the
  // whole thing, brand included, rather than having a suffix appended.
  if (!routePath) {
    return {
      title: copy.homeTitle || ORGANIZATION_NAME,
      description: homeDescription,
      isArticle: false,
    };
  }

  // A blog post. The index at `blog` shares the prefix and is not one.
  if (routePath.startsWith("blog/")) {
    const post = blogPosts.find((candidate) => candidate.slug === routePath.slice("blog/".length));

    return post
      ? {
          title: `${getLocalizedField(post, "title", lang)} | ${TITLE_BRAND}`,
          description: getLocalizedField(post, "summary", lang),
          isArticle: true,
        }
      : unresolved(homeDescription);
  }

  // A case study. Localised as a whole record rather than field by field,
  // because `localizeCaseStudy` merges a per-language details file into it.
  if (routePath.startsWith("case-studies/")) {
    const caseStudy = getLocalizedCaseStudyBySlug(
      routePath.slice("case-studies/".length),
      lang,
    );

    return caseStudy
      ? {
          title: `${caseStudy.title} | ${TITLE_BRAND}`,
          description: caseStudy.summary,
          isArticle: false,
        }
      : unresolved(homeDescription);
  }

  // Any other route, including the two index pages. Both halves are required:
  // a half-filled entry would otherwise pair a page title with the home
  // description, which reads as a correct page and is not one.
  const page = copy.pages?.[routePath];

  return page?.title && page?.description
    ? { title: `${page.title} | ${TITLE_BRAND}`, description: page.description, isArticle: false }
    : unresolved(homeDescription);
}

/**
 * Build every fact a route's ``<head>`` declares.
 *
 * Args:
 *     input: The page's pathname — ``/<lang>[/<route>]``, in any slash form —
 *         and the locale copy its title and description are drawn from.
 *
 * Returns:
 *     The complete metadata for that page, ready to be written as tags by
 *     whichever emitter asked for it.
 */
export function buildHeadMeta(input: { pathname: string; copy: RouteCopy }): HeadMeta {
  // The root document (CWS-237; supersedes the CWS-51 ``D3`` selector
  // framing): the English homepage experience served at the bare origin
  // and self-canonical ``https://shelpuk.com``. Its pathname carries no
  // locale segment at all — which ``splitLocalePath`` would otherwise
  // normalise to ``("en", "")``, the same parts as the home. Resolving
  // through ``resolveCopy`` would yield the home's title and description,
  // a byte-identical duplicate of ``/en`` that would trip the S14/S15
  // within-locale uniqueness guards. The carve-out derives from the
  // pathname shape, not from any flag, so every emitter agrees on the same
  // definition without a hand-maintained list of root paths.
  //
  // Deliberately accepts the broader class the URL normaliser already
  // collapses to the bare origin: ``"/"``, ``""``, ``"//"``, ``"///"`` — every
  // path of only empty segments, all of which ``toCanonicalUrl`` resolves
  // to ``SITE_ORIGIN``. Today's callers pass either ``location.pathname``
  // (always at least ``"/"``) or ``/${lang}${routePath ? … : ""}`` (never
  // bare); the loose match is inert for them, and the bare ``""`` is
  // unreachable from a router. The carve-out is the single thing keeping
  // ``/en`` from becoming a byte-equivalent duplicate of ``/``.
  const isRootDocument = input.pathname.split("/").every((segment) => segment === "");
  const { lang, routePath } = splitLocalePath(input.pathname);
  const { title, description, isArticle } = isRootDocument
    ? {
        title: ROOT_DOCUMENT_TITLE,
        description: ROOT_DOCUMENT_DESCRIPTION,
        isArticle: false,
      }
    : resolveCopy(routePath, lang, input.copy);

  // The public *path* is kept beside the URL so the emitters name the same art
  // in ``og:image`` and in ``twitter:image``. Every image on the site is
  // 1200×630 by policy (`ogImageDimensions.ts`), so the declared size is the
  // standard constant rather than a per-image lookup.
  const ogImagePath = getOgImageForRoute(routePath, lang) || getDefaultOgImagePath(lang);

  return {
    lang,
    routePath,
    title,
    description,
    // From the pathname, not from `lang` and `routePath` recomposed: a path
    // carrying no locale prefix must keep naming the page it named.
    canonicalUrl: toCanonicalUrl(input.pathname),
    ogImagePath,
    // `SITE_ORIGIN`, not `toCanonicalUrl`: this names an image *file*, so
    // slash-normalising it would be meaningless at best.
    ogImageUrl: `${SITE_ORIGIN}${ogImagePath}`,
    ogImageDimensions: STANDARD_OG_IMAGE_DIMENSIONS,
    ogType: isArticle ? "article" : "website",
    ogLocale: getOgLocale(lang),
    alternateOgLocales: getAlternateOgLocales(lang),
    alternates: buildHreflangAlternates(routePath),
    siteName: ORGANIZATION_NAME,
    twitterCard: TWITTER_CARD_TYPE,
  };
}
